
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import AssignedProjectCard from "../components/assetManager/AssignedProjectCard";
import PnlHistoryTable from "../components/assetManager/PnlHistoryTable";
import { useAssetManagerWorkspace } from "../hooks/useAssetManagerWorkspace";
import { nonApprovedPnlRows, sortPnlRowsNewestFirst } from "../utils/assetManagerPnl";

export default function AssetManagerAssignedProjectsPage() {
  const {
    assignedProjects,
    pnlRows,
    openPnlModal,
    openPnlEditModal,
    onCancelPnl,
    pnlCancellingId,
    message,
    load,
  } = useAssetManagerWorkspace();

  const [searchParams, setSearchParams] = useSearchParams();
  const [submittedProjectFilter, setSubmittedProjectFilter] = useState(
    () => searchParams.get("project") || ""
  );

  const submittedNotApproved = useMemo(
    () => sortPnlRowsNewestFirst(nonApprovedPnlRows(pnlRows)),
    [pnlRows]
  );

  const filteredSubmitted = useMemo(() => {
    if (!submittedProjectFilter) return submittedNotApproved;
    return submittedNotApproved.filter(
      (r) => String(r.project) === String(submittedProjectFilter)
    );
  }, [submittedNotApproved, submittedProjectFilter]);

  useEffect(() => {
    const pid = searchParams.get("project");
    if (searchParams.get("section") === "submitted") {
      if (pid) setSubmittedProjectFilter(pid);
      const el = document.getElementById("am-submitted-pnl");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [searchParams]);

  const goToSubmittedSection = (projectId = "") => {
    const params = new URLSearchParams();
    params.set("section", "submitted");
    if (projectId) params.set("project", projectId);
    setSearchParams(params, { replace: false });
    setSubmittedProjectFilter(projectId);
    requestAnimationFrame(() => {
      document.getElementById("am-submitted-pnl")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <div className="page">
      <h2>Assigned Projects</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        All projects currently assigned to you. Submit P&amp;L during fundraising or funded stages; submissions awaiting
        approval are listed below.
      </p>
      {message && <p className="msg">{message}</p>}
      <p style={{ marginBottom: 12 }}>
        <button type="button" className="btn-secondary compact-btn" onClick={() => load().catch(() => {})}>
          Refresh
        </button>
        {submittedNotApproved.length > 0 && (
          <button
            type="button"
            className="btn-secondary compact-btn"
            style={{ marginLeft: 8 }}
            onClick={() => goToSubmittedSection()}
          >
            Submitted P&amp;L ({submittedNotApproved.length})
          </button>
        )}
      </p>

      <section
        id="am-submitted-pnl"
        className="card"
        style={{ padding: 14, marginBottom: 20 }}
      >
        <h3 style={{ marginTop: 0 }}>Submitted P&amp;L (not approved)</h3>
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          Pending, updated, rejected, and cancelled submissions across your assigned projects. Approved P&amp;L is in{" "}
          <Link to="/asset-manager/history/pnl">My History → Approved P&amp;L</Link>.
        </p>
        {submittedNotApproved.length > 0 ? (
          <PnlHistoryTable
            rows={filteredSubmitted}
            assignedProjects={assignedProjects}
            projectFilter={submittedProjectFilter}
            onProjectFilterChange={setSubmittedProjectFilter}
            pnlCancellingId={pnlCancellingId}
            onEdit={openPnlEditModal}
            onCancel={onCancelPnl}
            emptyMessage="No open P&amp;L submissions for this project."
          />
        ) : (
          <p className="muted" style={{ marginBottom: 0 }}>
            No open submissions — all your P&amp;L records are approved or you have not submitted yet.
          </p>
        )}
      </section>

      <h3 style={{ fontSize: 16, marginBottom: 10 }}>Your projects</h3>
      <ul className="list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {assignedProjects.map((p) => (
          <AssignedProjectCard key={p.id} project={p} pnlRows={pnlRows} onSubmitPnl={openPnlModal} />
        ))}
        {assignedProjects.length === 0 && <li className="list-item muted">No projects assigned.</li>}
      </ul>
    </div>
  );
}
